import React, { useEffect, useState } from 'react';
import Icon from './icons';
import type { Project } from './types';
import type { Lp, LpInput, LpStatus } from './api';
import { listFps, createLpPortal } from './api';
import { lpCollect } from './lpMove';
import { portalItems } from './collect';
import LpItemsEditor from './LpItemsEditor';
import { portalOrigin, LOCKED_OFFICE, officeInfo } from './types';
import { typingPhone, fmtPhone } from '../leads/calc';
import { BOND_AXIS, TermFilter, TermPills, TermSeg, WITHDRAW_AXIS, bondOf, passes, termLabel, termMismatches, termsText, useTermFilter, withdrawOf } from './terms';
import type { Bond, Withdraw } from './terms';

/**
 * LpPool.tsx — 벤처인증 「LP 후보」 메모 (2026-10-08 지도사 「LP 로 참여하길 희망하는 회사, 소개 팀장님, 연락처 기재해놨다가 조합에서 LP 가 필요한 게 있으면 매칭」)
 *  한 줄 = 회사 · 담당자 · 연락처 · 소개자(팀장님 · 소속 · 연락처) · 희망 출자 · 상태 · 매칭 조합 · 메모
 *  저장은 Venture.tsx(onSave · onDelete → /api/venture/lp) — 프로젝트 목록이 「LP n」 칩으로 매칭 수를 보여 준다
 */
interface Props {
  rows: Lp[];
  projects: Project[];
  loaded: boolean;
  error?: string;
  onReload?: () => void;
  toast: (m: string) => void;
  onSave: (id: string | null, p: LpInput) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onPatchRow?: (id: string, patch: Partial<Lp>) => void;   // 링크를 만든 줄만 바로 고침(목록 다시 읽지 않고)
}

export const LP_STATUS: { v: LpStatus; label: string; cls: string }[] = [
  { v: 'wait', label: '대기', cls: 'p-blue' },
  { v: 'matched', label: '매칭', cls: 'p-amber' },
  { v: 'hold', label: '보류', cls: 'p-gray' },
  { v: 'done', label: '출자 완료', cls: 'p-green' },
];
const statusOf = (v: string) => LP_STATUS.find((s) => s.v === v) || LP_STATUS[0];
type Filter = 'all' | LpStatus;
const FILTER_KEY = 'venture.lpFilter';
const readFilter = (): Filter => { try { const v = localStorage.getItem(FILTER_KEY); return v && LP_STATUS.some((s) => s.v === v) ? (v as Filter) : 'all'; } catch { return 'all'; } };

const EMPTY: LpInput = { company: '', contact: '', phone: '', referrer: '', referrer_org: '', referrer_phone: '', amount: '', withdraw: '', bond: '', status: 'wait', project_id: null, memo: '' };

/** 연락처 → 숫자만 남긴 전화 링크 (휴대폰에서 바로 걸기) */
const telHref = (v: string) => { const d = (v || '').replace(/[^0-9+]/g, ''); return d.length >= 8 ? 'tel:' + d : ''; };

// 한글 IME 조합 중 마지막 글자 끊김 방지 (VentureList 와 같은 방식)
function Txt({ value, onChange, area, ...rest }: { value: string; onChange: (v: string) => void; area?: boolean } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = useState(value);
  const composing = React.useRef(false);
  useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  const common = {
    value: local,
    onCompositionStart: () => { composing.current = true; },
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => { composing.current = false; onChange(e.currentTarget.value); },
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); },
  };
  if (area) return <textarea {...common} rows={3} placeholder={rest.placeholder} maxLength={rest.maxLength} />;
  return <input {...common} {...rest} />;
}

/* 소개자(팀장님) 자동 입력 (2026-10-12 지도사 「FP 정보 자동 입력」) — 업무관리에 등록된 FP + 예전에 적은 소개자에서
   이름을 고르면 소속 · 연락처를 채운다(비어 있는 칸만 · 앞서 자동으로 채운 값은 바꿔 줌). 같은 이름이 둘이면 「이름 (소속)」으로 구분 */
interface RefPerson { name: string; org: string; phone: string }
function refOptions(fps: RefPerson[], rows: Lp[]): { value: string; p: RefPerson }[] {
  const all: RefPerson[] = fps.concat(rows.filter((r) => (r.referrer || '').trim()).map((r) => ({ name: r.referrer.trim(), org: (r.referrer_org || '').trim(), phone: (r.referrer_phone || '').trim() })));
  const seen = new Map<string, RefPerson>();
  all.forEach((p) => {
    const n = p.name.replace(/\s*(팀장|FP)님?$/, '').trim(); if (!n) return;
    const k = n + '|' + p.org;
    const cur = seen.get(k);
    if (!cur) seen.set(k, { name: n, org: p.org, phone: p.phone }); else if (!cur.phone && p.phone) cur.phone = p.phone;
  });
  const list = Array.from(seen.values());
  const cnt = new Map<string, number>(); list.forEach((p) => cnt.set(p.name, (cnt.get(p.name) || 0) + 1));
  return list.sort((a, b) => a.name.localeCompare(b.name, 'ko')).map((p) => ({ value: (cnt.get(p.name) || 0) > 1 && p.org ? `${p.name} (${p.org})` : p.name, p }));
}

export default function LpPool({ rows, projects, loaded, error, onReload, toast, onSave, onDelete, onPatchRow }: Props) {
  const [fps, setFps] = useState<RefPerson[]>([]);
  useEffect(() => { listFps().then((l) => setFps(l.map((f) => ({ name: f.name || '', org: f.company || '', phone: f.phone || '' })))).catch(() => { /* 목록 없이도 입력은 된다 */ }); }, []);
  const refOpts = refOptions(fps, rows);
  const lastFill = React.useRef<RefPerson | null>(null);
  /* 정보 받기 링크 (2026-10-12) — 없으면 만들고(조합 LP 카드와 같은 항목) 복사 · 조합에 매칭하면 이 포털이 그대로 조합 LP 카드로 간다 */
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [itemsFor, setItemsFor] = useState<Lp | null>(null);   // 「항목 편집」 창(2026-11-01)
  const copyLink = async (r: Lp) => {
    if (linkBusy) return;
    setLinkBusy(r.id);
    try {
      let pid = r.collect_pid || '';
      if (!pid) {
        const { c, ref } = lpCollect(r);
        pid = (await createLpPortal(r.id, portalItems(c, ref), 'roum')).collect_pid;
        if (onPatchRow) onPatchRow(r.id, { collect_pid: pid, portal: { total: portalItems(c, ref).length, done: 0 } });
      }
      const url = portalOrigin() + '/api/portal/' + pid + '/share';
      try { await navigator.clipboard.writeText(url); toast('정보 받기 링크를 복사했어요 — 카톡 · 문자로 보내세요'); } catch { toast(url); }
    } catch (e) { toast('⚠ 링크 만들기 실패 — ' + ((e as Error).message || '')); }
    finally { setLinkBusy(null); }
  };
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>(readFilter);
  const [edit, setEdit] = useState<{ id: string | null; f: LpInput; orig: string } | null>(null);
  const [wd, pickWd] = useTermFilter(WITHDRAW_AXIS, 'venture.lpWdFilter');   // 이체 구분 · 사채 처리 거르기(2026-10-28 · venture/terms)
  const [bond, pickBond] = useTermFilter(BOND_AXIS, 'venture.lpBondFilter');
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState<string | null>(null);
  useEffect(() => {
    if (!armed) return;
    const t = window.setTimeout(() => setArmed(null), 3500);
    return () => window.clearTimeout(t);
  }, [armed]);
  const pick = (v: Filter) => { setFilter(v); try { localStorage.setItem(FILTER_KEY, v); } catch { /* 화면은 동작 */ } };
  const orgOf = (pid: string | null | undefined) => { const p = pid ? projects.find((x) => x.id === pid) : null; return p ? p.org : ''; };
  const countOf = (v: Filter) => (v === 'all' ? rows.length : rows.filter((r) => r.status === v).length);
  const kw = q.trim().toLowerCase();
  // 이체 구분 필터(2026-10-28 「LP 대기 리스트에도 동일하게 구분」)
  const view = rows.filter((r) => (filter === 'all' || r.status === filter) && passes(WITHDRAW_AXIS, wd, r) && passes(BOND_AXIS, bond, r)
    && (!kw || [r.company, r.contact, r.phone, r.referrer, r.referrer_org, r.referrer_phone, r.amount, r.memo, orgOf(r.project_id)].join(' ').toLowerCase().includes(kw)));
  const set = (k: keyof LpInput, v: string | null) => setEdit((e) => (e ? { ...e, f: { ...e.f, [k]: v } } : e));
  const dirty = !!edit && JSON.stringify(edit.f) !== edit.orig;
  const open = (r: Lp | null) => {
    if (dirty) { toast('수정 중인 내용을 먼저 저장하거나 취소하세요'); return; }   // 입력하던 내용이 말없이 사라지지 않게
    const f: LpInput = r ? { company: r.company, contact: r.contact, phone: fmtPhone(r.phone), referrer: r.referrer, referrer_org: r.referrer_org, referrer_phone: fmtPhone(r.referrer_phone), amount: r.amount, withdraw: withdrawOf(r), bond: bondOf(r), status: r.status, project_id: r.project_id, memo: r.memo } : { ...EMPTY };
    setEdit({ id: r ? r.id : null, f, orig: JSON.stringify(f) });
  };
  const save = async () => {
    if (!edit || busy) return;
    if (!(edit.f.company || '').trim()) return;
    setBusy(true);
    // 조합을 고르면 상태를 「매칭」으로 (대기일 때만) · 조합을 비우면 「매칭」을 「대기」로
    const f = { ...edit.f };
    if (f.project_id && f.status === 'wait') f.status = 'matched';
    if (!f.project_id && f.status === 'matched') f.status = 'wait';   // 「매칭」은 조합을 골라야 고를 수 있다(아래 select) — 조합을 비웠을 때만 여기로 온다
    const ok = await onSave(edit.id, f);
    setBusy(false);
    if (ok) setEdit(null);
  };
  const projOpts = projects.slice().sort((a, b) => a.org.localeCompare(b.org, 'ko'));

  const form = edit ? (
    <div className="formcard lp-form">
      <div className="fc-h"><b>{edit.id ? 'LP 후보 수정' : '새 LP 후보'}</b><span className="hs">회사명만 넣어도 저장됩니다</span></div>
      <div className="lp-grid">
        <div className="fld span2"><label>회사명<span className="req">*</span></label><Txt value={edit.f.company || ''} onChange={(v) => set('company', v)} placeholder="(주)○○" maxLength={200} autoFocus /></div>
        <div className="fld"><label>담당자 · 대표</label><Txt value={edit.f.contact || ''} onChange={(v) => set('contact', v)} placeholder="홍길동 대표" maxLength={100} /></div>
        <div className="fld"><label>연락처</label><Txt value={edit.f.phone || ''} onChange={(v) => set('phone', typingPhone(v))} placeholder="010-0000-0000" maxLength={40} inputMode="tel" /></div>
        <div className="fld"><label>소개자 (팀장님)</label><Txt value={edit.f.referrer || ''} list="lp-ref-list" onChange={(v) => {
          const hit = refOpts.find((o) => o.value === v.trim());
          if (!hit) { set('referrer', v); return; }
          const prev = lastFill.current, p = hit.p;
          setEdit((e) => {
            if (!e) return e;
            const f = { ...e.f, referrer: p.name };
            if (p.org && (!(f.referrer_org || '').trim() || (prev && f.referrer_org === prev.org))) f.referrer_org = p.org;
            if (p.phone && (!(f.referrer_phone || '').trim() || (prev && f.referrer_phone === prev.phone))) f.referrer_phone = fmtPhone(p.phone);
            return { ...e, f };
          });
          lastFill.current = p;
        }} placeholder="이름을 치면 소속 · 연락처 자동" maxLength={100} />
          <datalist id="lp-ref-list">{refOpts.map((o) => <option key={o.value} value={o.value} label={[o.p.org, o.p.phone].filter(Boolean).join(' · ')} />)}</datalist></div>
        <div className="fld"><label>소개자 소속</label><Txt value={edit.f.referrer_org || ''} onChange={(v) => set('referrer_org', v)} placeholder="한화생명 ○○지점" maxLength={100} /></div>
        <div className="fld"><label>소개자 연락처</label><Txt value={edit.f.referrer_phone || ''} onChange={(v) => set('referrer_phone', typingPhone(v))} placeholder="010-0000-0000" maxLength={40} inputMode="tel" /></div>
        <div className="fld"><label>희망 출자 금액</label><Txt value={edit.f.amount || ''} onChange={(v) => set('amount', v)} placeholder="예: 3,000만원 · 1억 이내" maxLength={60} /></div>
        <div className="fld"><label>이체 구분</label>
          <TermSeg axis={WITHDRAW_AXIS} value={withdrawOf(edit.f)} onChange={(v) => set('withdraw', v as Withdraw)} label="이 LP 가 원하는 이체 구분" />
        </div>
        <div className="fld"><label>사채 처리</label>
          <TermSeg axis={BOND_AXIS} value={bondOf(edit.f)} onChange={(v) => set('bond', v as Bond)} label="이 LP 가 원하는 사채 처리" />
        </div>
        <div className="fld"><label>상태</label>
          <select value={edit.f.status || 'wait'} onChange={(e) => set('status', e.target.value)}>
            {LP_STATUS.map((s) => <option key={s.v} value={s.v} disabled={s.v === 'matched' && !edit.f.project_id}>{s.label}{s.v === 'matched' && !edit.f.project_id ? ' (조합을 고르면)' : ''}</option>)}
          </select>
        </div>
        <div className="fld"><label>매칭 조합</label>
          <select value={edit.f.project_id || ''} onChange={(e) => {
            const v = e.target.value || null;
            // 조합을 고르면 「대기」 → 「매칭」, 조합을 비우면 「매칭」 → 「대기」 (화면에서 바로 보이게)
            setEdit((cur) => (cur ? { ...cur, f: { ...cur.f, project_id: v, status: v && cur.f.status === 'wait' ? 'matched' : !v && cur.f.status === 'matched' ? 'wait' : cur.f.status } } : cur));
          }}>
            <option value="">— 아직 없음 —</option>
            {projOpts.map((p) => <option key={p.id} value={p.id}>{p.org}{termsText(p) ? ' · ' + termsText(p) : ''}{p.group ? ' · ' + p.group : ''}</option>)}
          </select>
          {(() => {   // 이체 구분 · 사채 처리가 다르면 알려 줌(2026-10-28 「LP 매칭할 때 참고」) — 막지는 않음
            const pj = edit.f.project_id ? projects.find((x) => x.id === edit.f.project_id) : null;
            return pj ? termMismatches(edit.f, pj).map((m) => <div key={m.axis.key} className="lp-wd-warn">이 조합은 「{termLabel(m.axis, m.project)}」 · 이 LP 는 「{termLabel(m.axis, m.lp)}」를 원해요</div>) : null;
          })()}
        </div>
        <div className="fld span2"><label>메모</label><Txt area value={edit.f.memo || ''} onChange={(v) => set('memo', v)} placeholder="출자 가능 시기 · 선호 업종 · 조건 등" maxLength={4000} /></div>
      </div>
      <div className="lp-foot">
        <button className="btn" onClick={() => setEdit(null)}>취소</button>
        <button className="btn pri" disabled={busy || !(edit.f.company || '').trim()} onClick={save}>{busy ? '저장 중…' : '저장'}</button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="page-h">
        <div>
          <h1>LP 후보</h1>
          <p>조합에 LP 가 필요할 때 연결할 회사 · 소개자 메모</p>
        </div>
        {rows.length ? (
          <div className="seg office-filt lp-filt" role="radiogroup" aria-label="상태별 보기">
            {([{ v: 'all' as Filter, label: '전체' }] as { v: Filter; label: string }[]).concat(LP_STATUS.map((s) => ({ v: s.v as Filter, label: s.label }))).map((o) => (
              <button key={o.v} type="button" className={filter === o.v ? 'on' : ''} aria-pressed={filter === o.v} onClick={() => pick(o.v)}>
                {o.label} <span className="cnt">{countOf(o.v)}</span>
              </button>
            ))}
          </div>
        ) : null}
        <TermFilter axis={WITHDRAW_AXIS} items={rows} value={wd} onPick={pickWd} />
        <TermFilter axis={BOND_AXIS} items={rows} value={bond} onPick={pickBond} />
        <button className="btn pri" onClick={() => open(null)}><Icon n="plus" /> LP 후보 추가</button>
      </div>
      {form && !edit?.id ? form : null}
      {!loaded ? <div className="empty"><p>불러오는 중…</p></div> : error ? (
        <div className="empty">
          <b>LP 후보를 불러오지 못했습니다</b>
          <p>{error}</p>
          {onReload ? <button className="btn" onClick={onReload} style={{ margin: '0 auto' }}>다시 불러오기</button> : null}
        </div>
      ) : rows.length === 0 && !edit ? (
        <div className="empty">
          <div className="eic"><Icon n="users" /></div>
          <b>아직 적어 둔 LP 후보가 없습니다</b>
          <p>LP 로 참여하고 싶다는 회사가 생기면 소개해 준 팀장님 · 연락처와 함께 적어 두세요. 조합에 LP 가 필요할 때 여기서 찾아 연결합니다.</p>
          <button className="btn pri" onClick={() => open(null)} style={{ margin: '0 auto' }}><Icon n="plus" /> LP 후보 추가</button>
        </div>
      ) : rows.length ? (
        <div className="tablewrap">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', background: '#fbfcfd' }}>
            <Txt value={q} onChange={setQ} placeholder="🔍 회사 · 소개자 · 연락처 · 메모 검색"
              style={{ width: '100%', maxWidth: 360, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--line-2)', fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--ink)', boxSizing: 'border-box' }} />
          </div>
          <table className="lp-table">
            <thead>
              <tr><th>회사 / 담당자</th><th>소개자</th><th>희망 출자 · 조건</th><th>정보 받기</th><th>상태 · 매칭 조합</th><th>메모</th><th></th></tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', fontSize: 13 }}>{kw ? '검색 결과가 없습니다' : statusOf(filter).label + ' 상태인 후보가 없습니다'}</td></tr>
              ) : view.map((r) => {
                const st = statusOf(r.status);
                const org = orgOf(r.project_id);
                if (edit && edit.id === r.id) return <tr key={r.id} className="lp-editing"><td colSpan={7}>{form}</td></tr>;
                return (
                  <tr key={r.id} onClick={() => open(r)}>
                    <td className="cell-name" data-l="회사">
                      <b>{r.company}</b>{!LOCKED_OFFICE && r.office === 'atv' ? <span className="pill lp-office" title="올댓벤처스에서 적은 후보">{officeInfo('atv').short}</span> : null}
                      {r.contact || r.phone ? (
                        <div className="sub">{r.contact}{r.contact && r.phone ? ' · ' : ''}{r.phone ? (telHref(r.phone) ? <a href={telHref(r.phone)} onClick={(e) => e.stopPropagation()}>{fmtPhone(r.phone)}</a> : r.phone) : null}</div>
                      ) : null}
                    </td>
                    <td data-l="소개자">
                      {r.referrer || r.referrer_org ? (
                        <div>
                          <div>{r.referrer || '-'}{r.referrer_org ? <span className="pill p-gray" style={{ marginLeft: 5 }}>{r.referrer_org}</span> : null}</div>
                          {r.referrer_phone ? <div className="sub">{telHref(r.referrer_phone) ? <a href={telHref(r.referrer_phone)} onClick={(e) => e.stopPropagation()}>{fmtPhone(r.referrer_phone)}</a> : r.referrer_phone}</div> : null}
                        </div>
                      ) : <span style={{ color: 'var(--muted)' }}>-</span>}
                    </td>
                    <td data-l="희망 출자">{r.amount || <span style={{ color: 'var(--muted)' }}>-</span>}{termsText(r) ? <div className="tm-pills"><TermPills x={r} /></div> : null}</td>
                    <td data-l="정보 받기" onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxWidth: 220 }}>
                        <button className="btn" style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap', justifyContent: 'center' }} disabled={linkBusy === r.id} onClick={() => void copyLink(r)}
                          title={r.collect_pid ? '받는 링크를 다시 복사합니다' : 'LP 로서 필요한 자료를 받는 링크를 만들고 복사합니다'}>
                          {linkBusy === r.id ? '만드는 중…' : r.collect_pid ? '🔗 링크 복사' : '🔗 링크 만들기'}
                        </button>
                        <button className="btn" style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap', justifyContent: 'center' }} onClick={() => setItemsFor(r)}
                          title="받을 자료(항목 · 설명 · 가이드 링크)를 여기서 바로 고칩니다">✏️ 항목 편집</button>
                      </div>
                      {r.portal ? <div className="sub" style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>받은 자료 {r.portal.done}/{r.portal.total}</div> : null}
                    </td>
                    <td data-l="상태">
                      <div>
                        <span className={'pill ' + st.cls}>{st.label}</span>
                        {org ? <div className="sub" style={{ marginTop: 4 }}>→ {org}{r.moved_project_id && r.moved_project_id === r.project_id ? ' · LP 로 옮김' : ''}</div> : null}
                        {(() => { const pj = r.project_id ? projects.find((x) => x.id === r.project_id) : null; return pj ? termMismatches(r, pj).map((m) => <div key={m.axis.key} className="lp-wd-warn">조합은 「{termLabel(m.axis, m.project)}」 — LP 와 달라요</div>) : null; })()}
                      </div>
                    </td>
                    <td data-l="메모">{r.memo ? <div className="lp-memo" title={r.memo}>{r.memo}</div> : <span style={{ color: 'var(--muted)' }}>-</span>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      {armed === r.id ? (
                        <button className="pill p-amber" style={{ cursor: 'pointer', border: 'none' }} onClick={async () => { if (await onDelete(r.id)) setArmed(null); }}>한 번 더 누르면 삭제</button>
                      ) : <button className="ghostdel" onClick={() => setArmed(r.id)}>삭제</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {itemsFor && <LpItemsEditor lp={itemsFor} toast={toast} onClose={() => setItemsFor(null)} onSaved={(pid, total) => { if (onPatchRow) onPatchRow(itemsFor.id, { collect_pid: pid, portal: { total, done: itemsFor.portal ? itemsFor.portal.done : 0 } }); }} />}
    </>
  );
}
